import React, { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { subDimensionMaturityLineOverallVersusBenchmark } from "../../../services/api-services";
import { toastEmitter } from "../../../utills/utilities";
import { API_RESPONSE } from "../../../utills/app-constant";
import generateChartImage from "./generateChartImage";
import Remark from "./Remark";
import DownloadGraphButton from "../../../user/pages/page/DownloadGraphButton";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const MaturityBenchmark = ({ selectedCompanyId, graphName, myhandleExport }) => {
  const [loading, setLoading] = useState(false);
  const [dimensionsData, setDimensionsData] = useState([]);
  const [remark, setRemark] = useState(""); // For remark text
  const [remarkId, setRemarkId] = useState(null); // For remarkId
  const [isAttempt, setIsAttempt] = useState("")


  const fetchMaturityData = async () => {
    setLoading(true);
    try {
      const response = await subDimensionMaturityLineOverallVersusBenchmark(selectedCompanyId, graphName);
      if (response?.data?.responseCode === 200) {
        setDimensionsData(response?.data?.responseBody);
        const remark = response?.data?.remark || "";
        const remarkId = response?.data?.remarkId || null;
        const isAttempt = response?.data?.isAttempt ?? 0;
        setIsAttempt(isAttempt);
        setRemark(remark);
        setRemarkId(remarkId);
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      fetchMaturityData();
    }
  }, [selectedCompanyId]);

  const generateChartOptions = (dimensionName, subDimensionData, overallData) => {
    const annotations = [
      // Overall data annotations
      {
        x: overallData.currentCompanyOverallCurrentAvg,
        y: "Overall",
        marker: {
          size: 12,
          fillColor: "#80c740",
          shape: "circle",
          strokeWidth: 0,
          offsetY: -4,
        },
        label: {
          text: `${overallData.currentCompanyOverallCurrentAvg}`,
          style: {
            fontSize: "10px",
            color: "#999999",
          },
        },
      },
      {
        x: overallData.previousCompaniesOverallCurrentAvg,
        y: "Overall",
        marker: {
          size: 12,
          fillColor: "#999999",
          shape: "star",
          strokeWidth: 0,
          offsetY: -4,
        },
        label: {
          text: `${overallData.previousCompaniesOverallCurrentAvg}`,
          style: {
            fontSize: "10px",
            color: "#5ccafd",
          },
        },
      },
      {
        x: overallData.currentCompanyOverallFutureAvg,
        y: "Overall",
        marker: {
          size: 12,
          fillColor: "#ffc000 ",
          shape: "circle",
          strokeWidth: 0,
          offsetY: -4,
        },
        label: {
          text: `${overallData.currentCompanyOverallFutureAvg}`,
          style: {
            fontSize: "10px",
            color: "#29cc27",
          },
        },
      },
      {
        x: overallData.previousCompaniesOverallFutureAvg,
        y: "Overall",
        marker: {
          size: 12,
          fillColor: "#5ccafd",
          shape: "star",
          strokeWidth: 0,
          offsetY: -4,
        },
        label: {
          text: `${overallData.previousCompaniesOverallFutureAvg}`,
          style: {
            fontSize: "10px",
            color: "#29cc27",
          },
        },
      },
    ];

    // Add subdimension-specific annotations
    subDimensionData.forEach((dataPoint) => {
      annotations.push(
        {
          x: dataPoint.currentCompanyCurrentAvg,
          y: dataPoint.subDimensionName,
          marker: {
            size: 12,
            fillColor: "#80c740",
            shape: "circle",
            strokeWidth: 0,
            offsetY: -4,
          },
          label: {
            text: `${dataPoint.currentCompanyCurrentAvg}`,
            style: {
              fontSize: "10px",
              color: "#999999",
            },
          },
        },
        {
          x: dataPoint.previousCompaniesCurrentAvg,
          y: dataPoint.subDimensionName,
          marker: {
            size: 13,
            fillColor: "#999999",
            shape: "star",
            strokeWidth: 0,
            offsetY: -4,
          },
          label: {
            text: `${dataPoint.previousCompaniesCurrentAvg}`,
            style: {
              fontSize: "10px",
              color: "#5ccafd",
            },
          },
        },
        {
          x: dataPoint.currentCompanyFutureAvg,
          y: dataPoint.subDimensionName,
          marker: {
            size: 12,
            fillColor: "#ffc000 ",
            shape: "circle",
            strokeWidth: 0,
            offsetY: -4,
          },
          label: {
            text: `${dataPoint.currentCompanyFutureAvg}`,
            style: {
              fontSize: "10px",
              color: "#29cc27",
            },
          },
        },
        {
          x: dataPoint.previousCompaniesFutureAvg,
          y: dataPoint.subDimensionName,
          marker: {
            size: 13,
            fillColor: "#5ccafd",
            shape: "star",
            strokeWidth: 0,
            offsetY: -4,
          },
          label: {
            text: `${dataPoint.previousCompaniesFutureAvg}`,
            style: {
              fontSize: "10px",
              color: "#29cc27",
            },
          },
        }
      );
    });

    return {
      chart: {
        offsetY: 0,
        height: 420,
        type: "rangeBar",
        id: `maturity_benchmark_line_${dimensionName.replace(/\s+/g, "_")}`,
        events: {
          mounted: () => {
            setTimeout(() => {
              generateChartImage(`maturity_benchmark_line_${dimensionName.replace(/\s+/g, "_")}`, 'maturity_benchmark_line', remark);
            }, 500); // Adjust delay as needed
          },
        },
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        pan: {
          enabled: false,
        },
      },
      colors: ["#EC7D31", "#36BDCB"],
      plotOptions: {
        bar: {
          horizontal: true,
          isDumbbell: true,
        },
      },
      annotations: {
        points: annotations,
      },
      title: {
        text: `${dimensionName}`,
      },
      legend: {
        show: false,
        series: true,
        position: "top",
        horizontalAlign: "center",
        markers: {
          width: 15,
          height: 15,
          radius: 6,
        },
      },
      grid: {
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      xaxis: {
        min: 0,
        max: 10,
        tickAmount: 21,
        labels: {
          formatter: function (value) {
            return `${Number(value)?.toFixed(2)}`;
          },
        },
      },
    };
  };

  const generateChartSeries = (subDimensionData, overallData) => {

    const overallSeries = [
      {
        x: "Overall",
        y: [overallData.currentCompanyOverallCurrentAvg, overallData.currentCompanyOverallFutureAvg],
        mid: overallData.previousCompaniesOverallCurrentAvg,
        high: overallData.previousCompaniesOverallFutureAvg,
      }
    ];

    const subDimensionSeries = subDimensionData.map((dataPoint) => ({
      x: dataPoint.subDimensionName,
      y: [dataPoint.currentCompanyCurrentAvg, dataPoint.currentCompanyFutureAvg],
      mid: dataPoint.previousCompaniesCurrentAvg,
      high: dataPoint.previousCompaniesFutureAvg,
    }));
    return [
      { data: overallSeries },
      { data: subDimensionSeries },
    ];
  };


  const exportSummaryToExcel = (data, fileName = "Maturity Benchmark Line") => {
    if (!data || !Array.isArray(data)) return;

    const heading = ["Maturity Benchmark Line"];
    const allRows = [heading, []];

    data.forEach(dimension => {
      allRows.push([`Dimension: ${dimension.dimensionName}`]);
      allRows.push([
        "Prev. Companies Current Avg",
        "Prev. Companies Future Avg",
        "Current Company Current Avg",
        "Current Company Future Avg"
      ]);
      allRows.push([
        dimension.previousCompaniesOverallCurrentAvg,
        dimension.previousCompaniesOverallFutureAvg,
        dimension.currentCompanyOverallCurrentAvg,
        dimension.currentCompanyOverallFutureAvg
      ]);
      allRows.push([]); // Spacing row

      allRows.push(["Sub-Dimension", "Current Value", "Future Value", "Prev. Curr Avg", "Prev. Fut Avg"]);

      dimension.benchmarkLineSubDimensionResponseList.forEach(sub => {
        allRows.push([
          sub.subDimensionName,
          sub.currentCompanyCurrentAvg,
          sub.currentCompanyFutureAvg,
          sub.previousCompaniesCurrentAvg,
          sub.previousCompaniesFutureAvg
        ]);
      });

      allRows.push([]); // spacing between dimensions
    });

    const worksheet = XLSX.utils.aoa_to_sheet(allRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  };


  // useEffect(() => {
  //   // Register this chart in the parent component's chartRefs
  //   if (chartContainerRef.current) {
  //     chartRef.current["maturityBenchmark"] = chartContainerRef.current;
  //   }
  // }, [chartContainerRef]);

  return (
    <div style={{ overflow: "hidden" }} >
      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      ) : (
        dimensionsData.map((dimension) => (
          <>
            <div className="d-flex justify-content-between">
              <h5>{dimension?.dimensionName} </h5>
              <div className="d-flex">
                <DownloadGraphButton chartID={`chart20-${dimension?.id}`} myhandleExport={myhandleExport} />
                <button className="mb-3 dbtn px-3 py-2 text-white bg-primary rounded border-0 cursor-pointer me-3" onClick={() => exportSummaryToExcel(dimensionsData)}>Download Excel</button>
              </div>

            </div>
            <div key={dimension.dimensionId} style={{ marginBottom: "40px" }} className={`pdf-subchart chart20-${dimension?.id}`} data-heading="Maturity Benchmark Line" data-remark={remark} >
              <div className="d-flex justify-content-center " style={{ gap: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", }}>
                  <span
                    style={{
                      width: "20px",
                      height: "20px",
                      backgroundColor: "#29cc27",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  ></span>
                  <span>Current Score</span>
                </div>
                <div className='' style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <span
                    style={{
                      width: "20px",
                      height: "20px",
                      backgroundColor: "#fdbf2d",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  ></span>
                  <span>Future Score</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <span
                    style={{
                      width: "20px",
                      height: "20px",
                      display: "inline-block",
                      clipPath:
                        "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
                      backgroundColor: "#a8a8a5",
                    }}
                  ></span>
                  <span>Current Benchmark (Org Level)</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <span
                    style={{
                      width: "20px",
                      height: "20px",
                      display: "inline-block",
                      clipPath:
                        "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
                      backgroundColor: "#5ccafd",
                    }}
                  ></span>
                  <span>Future Benchmark (Org Level)</span>
                </div>
              </div>
              <Chart
                series={generateChartSeries(dimension.benchmarkLineSubDimensionResponseList, {
                  currentCompanyOverallCurrentAvg: dimension.currentCompanyOverallCurrentAvg,
                  currentCompanyOverallFutureAvg: dimension.currentCompanyOverallFutureAvg,
                  previousCompaniesOverallCurrentAvg: dimension.previousCompaniesOverallCurrentAvg,
                  previousCompaniesOverallFutureAvg: dimension.previousCompaniesOverallFutureAvg,
                })}
                options={generateChartOptions(dimension.dimensionName, dimension.benchmarkLineSubDimensionResponseList, {
                  currentCompanyOverallCurrentAvg: dimension.currentCompanyOverallCurrentAvg,
                  currentCompanyOverallFutureAvg: dimension.currentCompanyOverallFutureAvg,
                  previousCompaniesOverallCurrentAvg: dimension.previousCompaniesOverallCurrentAvg,
                  previousCompaniesOverallFutureAvg: dimension.previousCompaniesOverallFutureAvg,
                })}
                type="rangeBar"
                height={420}

              />

            </div>
          </>

        ))
      )}
      <Remark selectedCompanyId={selectedCompanyId} graphName="MaturityBenchmarkLine" initialRemark={remark}
        remarkId={remarkId} isAttempt={isAttempt} setRemarkprops={setRemark} />

    </div>
  );
};

export default MaturityBenchmark;

